/**
 * Minesweeper Game — Main Container Component
 *
 * Orchestrates: difficulty selection → gameplay → win/loss.
 *
 * Features:
 * - Mine counter (total mines - flags placed)
 * - Timer (starts on first click, stops on win/loss, max 999)
 * - Smiley face button (😊 playing, 😎 won, 😵 lost) — click to restart
 * - Flag mode toggle for mobile
 * - ErrorBoundary wrapping
 * - GameSessionManager integration
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { MinesweeperEngine } from '../MinesweeperEngine';
import { MinesweeperBoard } from './MinesweeperBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { MinesweeperErrorHandler } from '../services/MinesweeperErrorHandler';
import { MinesweeperStatisticsService } from '../services/MinesweeperStatisticsService';
import { MinesweeperGamePersistence } from '../services/MinesweeperGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import UniversalGameLayout, { UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import {
  MinesweeperDifficulty,
  MinesweeperGameSettings,
  MinesweeperGameState,
  MinesweeperGameProps,
} from '../types';
import './MinesweeperGame.css';

type GameView = 'mode-select' | 'playing' | 'completed';

const MinesweeperGameInner: React.FC<MinesweeperGameProps> = ({ onExit }) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<MinesweeperGameSettings | null>(null);
  const [gameState, setGameState] = useState<MinesweeperGameState | null>(null);
  const [timer, setTimer] = useState(0);
  const [flagMode, setFlagMode] = useState(false);

  // Refs
  const engineRef = useRef<MinesweeperEngine>(new MinesweeperEngine());
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Services (lazy init)
  const errorHandlerRef = useRef<MinesweeperErrorHandler | null>(null);
  const statsServiceRef = useRef<MinesweeperStatisticsService | null>(null);
  const persistenceRef = useRef<MinesweeperGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new MinesweeperErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new MinesweeperStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new MinesweeperGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Minesweeper services:', e);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Timer management — starts on first click, stops on win/loss
  useEffect(() => {
    if (
      view === 'playing' &&
      gameState?.gameStatus === 'playing' &&
      gameState?.firstClick
    ) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          const newTime = Math.min(prev + 1, 999);
          engineRef.current.updateTimer(newTime);
          return newTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [view, gameState?.gameStatus, gameState?.firstClick]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: MinesweeperGameSettings) => {
      const engine = engineRef.current;
      const state = engine.initializeGame(newSettings.difficulty);

      setSettings(newSettings);
      setGameState(state);
      setTimer(0);
      setFlagMode(false);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('minesweeper');

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Ready',
        message: `${newSettings.difficulty.charAt(0).toUpperCase() + newSettings.difficulty.slice(1)} board ready. Click any cell to start!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (!gameState) return;
      if (gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') return;

      const engine = engineRef.current;

      // In flag mode (mobile), toggle flag instead of reveal
      if (flagMode) {
        const newState = engine.toggleFlag(row, col);
        setGameState(newState);
        return;
      }

      const newState = engine.revealCell(row, col);
      setGameState(newState);

      // Check for game over
      if (newState.gameStatus === 'lost') {
        handleGameLost();
      } else if (newState.gameStatus === 'won') {
        handleGameWon(newState);
      }
    },
    [gameState, flagMode]
  );

  const handleCellRightClick = useCallback(
    (row: number, col: number) => {
      if (!gameState) return;
      if (gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') return;

      const engine = engineRef.current;
      const newState = engine.toggleFlag(row, col);
      setGameState(newState);
    },
    [gameState]
  );

  const handleGameWon = useCallback(
    (state: MinesweeperGameState) => {
      setView('completed');

      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Record statistics
      if (statsServiceRef.current && settings) {
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            difficulty: settings.difficulty,
            won: true,
            time: timer,
            completedAt: Date.now(),
          },
          isAuthenticated ? user?.id : undefined
        );
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎉 You Win!',
        message: `Cleared the board in ${formatTime(timer)}!`,
        duration: 5000,
      });
    },
    [addNotification, settings, timer, isAuthenticated, user]
  );

  const handleGameLost = useCallback(() => {
    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // End game session
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
      gameSessionIdRef.current = null;
    }

    // Record statistics
    if (statsServiceRef.current && settings) {
      statsServiceRef.current.recordGameResult(
        {
          gameId: gameState?.gameId || '',
          difficulty: settings.difficulty,
          won: false,
          time: timer,
          completedAt: Date.now(),
        },
        isAuthenticated ? user?.id : undefined
      );
    }

    addNotification({
      type: NotificationType.ERROR,
      title: '💥 Game Over',
      message: 'You hit a mine!',
      duration: 3000,
    });
  }, [addNotification, settings, timer, isAuthenticated, user, gameState?.gameId]);

  const handleNewGame = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (settings) {
      // Restart with same difficulty
      const engine = engineRef.current;
      const state = engine.initializeGame(settings.difficulty);
      setGameState(state);
      setTimer(0);
      setFlagMode(false);
      setView('playing');

      gameSessionIdRef.current = gameSessionManager.current.startGameSession('minesweeper');
    } else {
      setView('mode-select');
      setGameState(null);
      setSettings(null);
      setTimer(0);
      setFlagMode(false);
    }
  }, [settings]);

  const handleChangeDifficulty = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setView('mode-select');
    setGameState(null);
    setSettings(null);
    setTimer(0);
    setFlagMode(false);
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Smiley face
  // =========================================================================

  const getSmiley = (): string => {
    if (!gameState) return '😊';
    switch (gameState.gameStatus) {
      case 'won':
        return '😎';
      case 'lost':
        return '😵';
      default:
        return '😊';
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const minesweeperConfig: GameModeConfig = {
      gameName: 'Minesweeper',
      gameIcon: '💣',
      availableModes: ['ai'],
      difficulties: [
        { id: 'beginner', label: 'Beginner', icon: '🌱', description: '9×9, 10 mines' },
        { id: 'intermediate', label: 'Intermediate', icon: '⭐', description: '16×16, 40 mines' },
        { id: 'expert', label: 'Expert', icon: '👑', description: '30×16, 99 mines' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.difficulty) {
        handleStartGame({ difficulty: selection.difficulty as MinesweeperDifficulty });
      }
    };

    return (
      <div className="ms-game">
        <div className="ms-game__header">
          <h2 className="ms-game__title">💣 Minesweeper</h2>
        </div>
        <UniversalGameModeSelector
          config={minesweeperConfig}
          onStart={handleModeSelection}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const minesRemaining = gameState.mineCount - gameState.flagCount;
  const isGameOver = gameState.gameStatus === 'won' || gameState.gameStatus === 'lost';

  // Game over config
  const gameOverConfig = isGameOver ? {
    show: true,
    result: (gameState.gameStatus === 'won' ? 'win' : 'loss') as 'win' | 'loss',
    title: gameState.gameStatus === 'won' ? 'You Win!' : 'Game Over',
    subtitle: gameState.gameStatus === 'won'
      ? `Cleared in ${formatTime(timer)} | ${settings?.difficulty}`
      : 'You hit a mine!',
    icon: gameState.gameStatus === 'won' ? '🎉' : '💥',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Minesweeper',
    board: (
      <div className="ms-game__board-wrapper">
        {/* Flag mode toggle for mobile */}
        <div className="ms-game__flag-toggle-area">
          <button
            className={`ms-game__flag-toggle ${flagMode ? 'ms-game__flag-toggle--active' : ''}`}
            onClick={() => setFlagMode((prev) => !prev)}
            aria-label={`Flag mode ${flagMode ? 'on' : 'off'}`}
            aria-pressed={flagMode}
          >
            🚩 {flagMode ? 'Flag Mode ON' : 'Flag Mode'}
          </button>
        </div>
        <MinesweeperBoard
          board={gameState.board}
          gameStatus={gameState.gameStatus}
          deathCell={gameState.deathCell}
          onCellClick={handleCellClick}
          onCellRightClick={handleCellRightClick}
          difficulty={gameState.difficulty}
        />
      </div>
    ),
    statusBar: {
      turnIndicator: (
        <span>
          💣 {String(Math.max(minesRemaining, 0)).padStart(3, '0')}
          {' | '}
          {getSmiley()}
          {' | '}
          ⏱️ {String(Math.min(timer, 999)).padStart(3, '0')}
        </span>
      ),
      gameStatus: isGameOver ? (gameState.gameStatus === 'won' ? 'Victory!' : 'Defeat') : undefined,
      gameStatusType: isGameOver ? 'ended' : 'active',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'difficulty', label: 'Difficulty', icon: '⚙️', onClick: handleChangeDifficulty, variant: 'secondary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="ms-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Format seconds as MM:SS */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Wrapped in ErrorBoundary */
const MinesweeperGame: React.FC<MinesweeperGameProps> = (props) => (
  <ErrorBoundary componentName="Minesweeper">
    <MinesweeperGameInner {...props} />
  </ErrorBoundary>
);

export { MinesweeperGame };
export default MinesweeperGame;
